import clsx from "clsx";
import type { FC } from "react";

import { StudentOptionsMenu } from "components/materials/page/groups/StudentOptionsMenu";
import {
  DraggablePageGroupUserType,
  DraggableSectionUserType,
} from "components/materials/page/groups/helpers";
import { isUserOnline, userActivityIcons } from "components/materials/section/helpers";
import { t } from "i18n/i18n";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { ListItem } from "mds/components/ListItem";
import { LongFormPencilIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  pageGroupUserSelectors,
  selectActivePageIdForUser,
  selectActivityStatusForUser,
} from "store/selectors";
import { selectUserBySectionUserId } from "store/selectors/section-user";
import { useObjectSelector } from "store/store-hooks";
import { getUserFullName } from "utils/user-utils";

interface PageGroupMemberProps {
  pageGroupUserId: string;
  order: number;
  pageGroupId: string;
  pageId: string;
  groupPageId: string;
  isReadOnly: boolean;
  onRemove?: (studentSectionUserId: string) => void;
  onDropSectionUser: (
    droppedItem: DraggableSectionUserType | DraggablePageGroupUserType,
    toIndex: number,
    sourceContainerId: string,
  ) => void;
}

export const PageGroupMember: FC<PageGroupMemberProps> = ({
  pageGroupUserId,
  order,
  pageGroupId,
  pageId,
  groupPageId,
  isReadOnly,
  onRemove,
  onDropSectionUser,
}) => {
  const pageGroupUser = useObjectSelector(
    (state) => pageGroupUserSelectors.selectById(state, pageGroupUserId),
    ["id", "section_user_id", "page_group_id", "section_id"],
  );
  const user = useObjectSelector(
    (state) => selectUserBySectionUserId(state, pageGroupUser?.section_user_id ?? null),
    ["id", "first_name", "last_name", "email"],
  );
  const userActivityType = useAppSelector((state) => selectActivityStatusForUser(state, user?.id));
  const userActivePageId = useAppSelector((state) => selectActivePageIdForUser(state, user?.id));

  // Fresh object each render so a cancelled drag's hover mutations on page_group_id cannot
  // persist into the next drag (useMemo would keep the mutated object after isDragging ends).
  const dragItem: DraggablePageGroupUserType | null = pageGroupUser
    ? { ...pageGroupUser, order }
    : null;

  if (!pageGroupUser || !dragItem) {
    return null;
  }

  /**
   * To be an active page group user (i.e. for the student to have a blue
   * draft icon next to their name), they must be online and on the group page.
   */
  const isPageGroupUserActive = userActivePageId === groupPageId && isUserOnline(userActivityType);

  return (
    <DragAndDrop
      className="w-full"
      draggable={!isReadOnly}
      dragItemKind="section_user"
      dropContainerId={pageGroupId}
      item={dragItem}
      onDropItem={onDropSectionUser}
    >
      <ListItem
        className={clsx(
          "@max-[460px]/group-config:h-auto @max-[460px]/group-config:min-h-9 @max-[460px]/group-config:px-0 @max-[460px]/group-config:text-[14px] flex w-full items-center justify-between",
          isPageGroupUserActive && "bg-blue-tint-95",
        )}
        data-section-user-id={pageGroupUser.section_user_id}
        data-testid={`row-${pageGroupUser.section_user_id}`}
        grabbable={!isReadOnly}
        title={
          isPageGroupUserActive
            ? t("group_configuration_card.tooltip.user_is_ready")
            : t("group_configuration_card.tooltip.user_is_not_ready")
        }
      >
        <div className="flex min-w-0 items-center gap-3">
          {userActivityIcons[userActivityType]}
          <div className="@max-[460px]/group-config:min-w-0 @max-[460px]/group-config:whitespace-normal @max-[460px]/group-config:[overflow-wrap:anywhere] min-w-12 overflow-hidden truncate">
            {getUserFullName(user)}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {isPageGroupUserActive ? <LongFormPencilIcon className="text-blue" /> : null}
          <StudentOptionsMenu
            currentGroupId={pageGroupId}
            isReadOnly={isReadOnly}
            pageId={pageId}
            studentSectionUserId={pageGroupUser.section_user_id}
            isInGroup
            onRemove={onRemove}
          />
        </div>
      </ListItem>
    </DragAndDrop>
  );
};
